/**
 * @fileoverview URL utility functions for parsing, serialization, and API URL generation.
 */

import {store, select} from "../store";
import {reactAppEnvironment} from "../config";
import {ASSETS_BUCKET_URL} from "./constants";
import {
  ADMIN_RATES_DOCUMENT_ROUTE,
  ADMISSION_RATES_DOCUMENT_ROUTE,
} from "../pages/page-rates-document/route";

/**
 * Parses a URL string into a URL object.
 *
 * @param {string} urlStr The URL string to parse.
 * @returns {URL} The parsed URL object.
 */

export const parse = (urlStr: string) => {
  return new URL(urlStr);
};

/**
 * Serializes a URL object back to a string.
 *
 * @param {URL} urlObj The URL object to serialize.
 * @returns {string} The serialized URL string.
 */
export const serialize = (urlObj: URL) => {
  return urlObj.toString();
};

/**
 * Converts a path to a full API URL.
 *
 * @param {string} path The API path to convert.
 * @returns {string} The full API URL.
 */
export const toApi = (path: string) => {
  const url = new URL(import.meta.env.VITE_API_URL);

  url.pathname = `${url.pathname}/${path}`.replace("//", "/");

  return url.toString();
};

/**
 * Adds access token to a URL for authentication.
 * TODO: this is ugly
 *
 * @param {string} urlStr The URL string to add token to.
 * @returns {string} The URL with access token parameter.
 */
export const withAccessToken = (urlStr: string) => {
  const {adminToken, admissionToken} = select((models: any) => ({
    adminToken: models.adminSession.getToken,
    admissionToken: models.admissionSession.getToken,
  }))(store.getState());
  const url = parse(urlStr);

  url.searchParams.set("accessToken", adminToken || admissionToken);

  return serialize(url);
};

/**
 * Legacy rates-document links, built into admission answers from template facility
 * properties (`basicRatesDocument`). The Java endpoint behind them was removed with the
 * document service rewrite, and "Basic_Rates"/"Facility_Rates" never matched its rates
 * route anyway, so these are routed to the app's rates page instead.
 */
const LEGACY_RATES_DOCUMENT_URL =
  /\/api\/v3\/documents\/([0-9a-f-]{36})\/generate\/(?:rates|Basic_Rates|Facility_Rates)\/?(?:[?#].*)?$/i;

/**
 * Maps a legacy rates-document link to the app page that generates the rates PDF,
 * within the admin or resident area the link is being rendered in.
 *
 * @param {string} urlStr The URL string to map.
 * @returns {string | null} The app URL, or null if this isn't a legacy rates link.
 */
export const toRatesDocumentUrl = (urlStr: string) => {
  const match = urlStr.match(LEGACY_RATES_DOCUMENT_URL);

  if (!match) {
    return null;
  }

  const route = window.location.pathname.startsWith("/admin")
    ? ADMIN_RATES_DOCUMENT_ROUTE
    : ADMISSION_RATES_DOCUMENT_ROUTE;

  return `${window.location.origin}${route.replace(":admissionId", match[1])}`;
};

/**
 * Transforms signature/file URLs from old format to new API Gateway format
 * and adds access token for authentication.
 * Converts /api/files/{id} to {API_GATEWAY_URL}/v3/files/{id}?accessToken={token}
 * Legacy rates-document links are mapped to the app's rates page (no token in the URL).
 *
 * @param {string} urlStr The URL string to transform.
 * @returns {string} The transformed URL with API Gateway endpoint and access token.
 */
export const withAccessTokenAndApiGateway = (urlStr: string) => {
  const ratesDocumentUrl = toRatesDocumentUrl(urlStr);

  if (ratesDocumentUrl) {
    return ratesDocumentUrl;
  }

  // Transform URL from /api/files/ to API Gateway /v3/files/
  const transformedUrl = urlStr.replace(
    `${import.meta.env.VITE_API_URL}/files/`,
    `${import.meta.env.VITE_API_GATEWAY_URL}/v3/files/`,
  );

  return withAccessToken(transformedUrl);
};

/**
 * Transforms v3 file URLs to full API Gateway URLs with access token.
 * Converts /v3/files/{id} to {API_GATEWAY_URL}/v3/files/{id}?accessToken={token}
 *
 * @param {string} urlStr The URL string to transform (e.g., "/v3/files/123")
 * @returns {string} The transformed URL with API Gateway endpoint and access token.
 */
export const transformV3FileUrl = (urlStr: string) => {
  // If it's already a full URL, just add access token
  if (urlStr.startsWith("http")) {
    return withAccessToken(urlStr);
  }

  // Transform relative /v3/files/ path to full API Gateway URL
  const fullUrl = `${import.meta.env.VITE_API_GATEWAY_URL}${urlStr}`;
  return withAccessToken(fullUrl);
};

/**
 * Gets the URL for the current environment by replacing bucket names.
 *
 * @param {string} [url=""] The URL to process.
 * @returns {string} The URL with environment-specific bucket name.
 */
export const getUrlForEnv = (url = "") => {
  const nextBucketName =
    ASSETS_BUCKET_URL[reactAppEnvironment] || ASSETS_BUCKET_URL.PRODUCTION;

  return url.replace(ASSETS_BUCKET_URL.PRODUCTION, nextBucketName);
};
